import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { incidentsAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDateTime, formatDate } from '../utils/formatDateTime';
import TimezonePill from '../components/TimezonePill';
import {
  PlusIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  PaperClipIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  BellAlertIcon,
  PhotoIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const incidentTypes = [
  { value: 'near_miss', label: 'Near Miss' },
  { value: 'first_aid', label: 'First Aid' },
  { value: 'medical_treatment', label: 'Medical Treatment' },
  { value: 'restricted_work', label: 'Restricted Work' },
  { value: 'lost_time', label: 'Lost Time Injury' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'environmental', label: 'Environmental' },
];

const severityLevels = [
  { value: 'minor', label: 'Minor', color: 'blue' },
  { value: 'moderate', label: 'Moderate', color: 'yellow' },
  { value: 'serious', label: 'Serious', color: 'orange' },
  { value: 'critical', label: 'Critical', color: 'red' },
];

// CSV Export Helper
const generateIncidentsCSV = (incidents) => {
  if (!incidents || incidents.length === 0) {
    return 'No incidents to export';
  }

  const headers = [
    'ID',
    'Title',
    'Type',
    'Severity',
    'Status',
    'Location',
    'Occurred At',
    'Reported At',
    'Investigator',
    'Investigation Due',
    'Root Cause',
    'Description',
  ];

  const rows = incidents.map(incident => [
    incident.id,
    `"${incident.title}"`,
    incident.type,
    incident.severity,
    incident.status,
    incident.location || '',
    formatDateTime(incident.occurredAt),
    formatDateTime(incident.reportedAt),
    incident.investigator || '',
    incident.investigationDue ? formatDateTime(incident.investigationDue) : '',
    `"${incident.rootCause || ''}"`,
    `"${incident.description || ''}"`,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  return csvContent;
};

export default function IncidentsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showEditInvestigation, setShowEditInvestigation] = useState(false);
  const [showStatusTransition, setShowStatusTransition] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    severity: 'all',
    investigator: '',
    dateRange: { start: '', end: '' },
  });

  const { selectedSite } = useSiteStore();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['incidents', selectedSite?.id, filters, searchQuery],
    queryFn: () => incidentsAPI.getIncidents({
      siteId: selectedSite?.id,
      status: filters.status !== 'all' ? filters.status : undefined,
      type: filters.type !== 'all' ? filters.type : undefined,
      severity: filters.severity !== 'all' ? filters.severity : undefined,
      startDate: filters.dateRange.start || undefined,
      endDate: filters.dateRange.end || undefined,
      search: searchQuery || undefined,
      investigator: filters.investigator || undefined,
    }),
  });

  const incidents = data?.data?.data || [];

  const statusCounts = {
    open: incidents.filter(i => i.status === 'open').length,
    investigating: incidents.filter(i => i.status === 'investigating').length,
    pending_action: incidents.filter(i => i.status === 'pending_action').length,
    resolved: incidents.filter(i => i.status === 'resolved').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold gradient-text">Incident Management</h1>
            <TimezonePill />
          </div>
          <p className="text-slate-700 mt-1">Track and investigate safety incidents</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Report Incident
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Open"
          value={statusCounts.open}
          color="red"
          index={0}
        />
        <StatCard
          title="Investigating"
          value={statusCounts.investigating}
          color="yellow"
          index={1}
        />
        <StatCard
          title="Pending Action"
          value={statusCounts.pending_action}
          color="orange"
          index={2}
        />
        <StatCard
          title="Resolved"
          value={statusCounts.resolved}
          color="green"
          index={3}
        />
      </div>

      {/* Search & Filters */}
      <div className="space-y-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        {/* Search Bar */}
        <div className="glass-card p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-600" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search incidents..."
                className="input w-full pl-10"
              />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="btn btn-secondary flex items-center gap-2 flex-1 sm:flex-none"
              >
                <DocumentTextIcon className="w-4 h-4" />
                Advanced
              </button>
              <button
                onClick={() => {
                  const csv = generateIncidentsCSV(incidents);
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `incidents-${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="btn btn-secondary flex items-center gap-2 flex-1 sm:flex-none"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="glass-card p-4 flex flex-wrap gap-2">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="input w-auto min-w-[150px]"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="investigating">Investigating</option>
            <option value="pending_action">Pending Action</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="input w-auto min-w-[150px]"
          >
            <option value="all">All Types</option>
            {incidentTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            className="input w-auto min-w-[150px]"
          >
            <option value="all">All Severity</option>
            {severityLevels.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="btn btn-secondary flex items-center gap-1"
            >
              Clear Search
            </button>
          )}

          {(filters.status !== 'all' || filters.type !== 'all' || filters.severity !== 'all') && (
            <button
              onClick={() => setFilters({ status: 'all', type: 'all', severity: 'all', investigator: '', dateRange: { start: '', end: '' } })}
              className="btn btn-secondary flex items-center gap-1"
            >
              Reset Filters
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="glass-card p-4 space-y-4">
            <h4 className="font-semibold text-slate-800">Advanced Filters</h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Investigator</label>
                <input
                  type="text"
                  value={filters.investigator}
                  onChange={(e) => setFilters({ ...filters, investigator: e.target.value })}
                  placeholder="Filter by investigator name..."
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Date Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, start: e.target.value } })}
                    className="input flex-1"
                  />
                  <input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, end: e.target.value } })}
                    className="input flex-1"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowAdvancedFilters(false)}
              className="btn btn-secondary text-sm"
            >
              Close Advanced Filters
            </button>
          </div>
        )}
      </div>

      {/* Incidents List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="glass-card p-6 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="flex gap-2">
                    <div className="h-6 w-20 bg-slate-100 rounded-full" />
                    <div className="h-6 w-24 bg-slate-100 rounded-full" />
                  </div>
                  <div className="h-5 w-3/4 bg-slate-100 rounded" />
                  <div className="h-4 w-1/2 bg-slate-100 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : incidents.length === 0 ? (
        <div className="glass-card p-12 text-center animate-scale-in">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-10 h-10 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">No incidents found</h3>
          <p className="text-slate-700 mb-6">Great job! No incidents match your current filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident, index) => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              index={index}
              onClick={() => setSelectedIncident(incident)}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateIncidentModal
          siteId={selectedSite?.id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedIncident && (
        <IncidentDetailModal
          incident={selectedIncident}
          onClose={() => setSelectedIncident(null)}
          onEditInvestigation={() => setShowEditInvestigation(true)}
          onStatusTransition={() => setShowStatusTransition(true)}
          onFileUpload={() => setShowFileUpload(true)}
        />
      )}

      {/* Edit Investigation Modal */}
      {showEditInvestigation && selectedIncident && (
        <EditInvestigationModal
          incident={selectedIncident}
          onClose={() => setShowEditInvestigation(false)}
          onSuccess={() => {
            setShowEditInvestigation(false);
            // Invalidate the specific incident to refresh the detail view
            queryClient.invalidateQueries({ queryKey: ['incidents', selectedIncident.id] });
            // Also invalidate the list to keep in sync
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
          }}
        />
      )}

      {/* Status Transition Modal */}
      {showStatusTransition && selectedIncident && (
        <StatusTransitionModal
          incident={selectedIncident}
          onClose={() => setShowStatusTransition(false)}
          onSuccess={() => {
            setShowStatusTransition(false);
            // Invalidate the specific incident to refresh the detail view
            queryClient.invalidateQueries({ queryKey: ['incidents', selectedIncident.id] });
            // Also invalidate the list to keep in sync
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
          }}
        />
      )}

      {/* File Upload Modal */}
      {showFileUpload && selectedIncident && (
        <FileUploadModal
          incident={selectedIncident}
          onClose={() => setShowFileUpload(false)}
          onSuccess={() => {
            setShowFileUpload(false);
            // Invalidate the specific incident to refresh the detail view
            queryClient.invalidateQueries({ queryKey: ['incidents', selectedIncident.id] });
            // Also invalidate the list to keep in sync
            queryClient.invalidateQueries({ queryKey: ['incidents'] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({ title, value, color, index }) {
  const colorConfig = {
    red: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-600' },
    yellow: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-600' },
    orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-600' },
    green: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-600' },
  };

  const config = colorConfig[color];

  return (
    <div
      className={clsx(
        'glass-card p-4 transition-all duration-300 hover:scale-[1.02] animate-slide-up',
        value > 0 && color === 'red' && 'border-red-500/50'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={clsx(
        'w-10 h-10 rounded-xl flex items-center justify-center mb-3',
        config.bg, config.border, 'border'
      )}>
        <span className={clsx('text-lg font-bold', config.text)}>{value}</span>
      </div>
      <p className="text-sm text-slate-700">{title}</p>
    </div>
  );
}

function IncidentCard({ incident, index, onClick }) {
  const severityConfig = {
    minor: 'badge-low',
    moderate: 'badge-medium',
    serious: 'badge-high',
    critical: 'badge-critical',
  };

  const statusConfig = {
    open: { bg: 'bg-red-500/20', text: 'text-red-600', border: 'border-red-500/30' },
    investigating: { bg: 'bg-yellow-500/20', text: 'text-yellow-600', border: 'border-yellow-500/30' },
    pending_action: { bg: 'bg-orange-500/20', text: 'text-orange-600', border: 'border-orange-500/30' },
    resolved: { bg: 'bg-green-500/20', text: 'text-green-600', border: 'border-green-500/30' },
    closed: { bg: 'bg-gray-500/20', text: 'text-gray-600', border: 'border-gray-500/30' },
  };

  const status = statusConfig[incident.status] || statusConfig.open;

  return (
    <div
      className="glass-card p-5 hover:scale-[1.01] transition-all duration-300 cursor-pointer animate-fade-in"
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={clsx('badge', severityConfig[incident.severity])}>
              {incident.severity}
            </span>
            <span className={clsx('badge', status.bg, status.text, 'border', status.border)}>
              {incident.status.replace('_', ' ')}
            </span>
            <span className="text-sm text-slate-700">
              {incidentTypes.find(t => t.value === incident.type)?.label}
            </span>
          </div>
          <h3 className="font-semibold text-slate-800 text-lg mb-2">{incident.title}</h3>
          <p className="text-sm text-slate-700 line-clamp-2">
            {incident.description}
          </p>
        </div>
        <div className="flex sm:flex-col items-center sm:items-end gap-3 text-sm text-slate-700">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="w-4 h-4" />
            {formatDate(incident.occurredAt)}
          </div>
          {incident.location && (
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-4 h-4" />
              {incident.location}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateIncidentModal({ siteId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'near_miss',
    severity: 'minor',
    location: '',
    occurredAt: new Date().toISOString().slice(0, 16),
  });

  const mutation = useMutation({
    mutationFn: (data) => incidentsAPI.createIncident({ ...data, siteId }),
    onSuccess: () => {
      toast.success('Incident reported successfully');
      onSuccess();
    },
    onError: () => {
      toast.error('Failed to report incident');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-lg max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">Report Incident</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-700 hover:text-slate-800 hover:bg-slate-50 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[calc(90vh-160px)]">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input"
              placeholder="Brief description of the incident"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="input"
              >
                {incidentTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="input"
              >
                {severityLevels.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="input"
              placeholder="e.g., Zone A, Level 3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Date & Time</label>
            <input
              type="datetime-local"
              value={formData.occurredAt}
              onChange={(e) => setFormData({ ...formData, occurredAt: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input h-24 resize-none"
              placeholder="Describe what happened..."
            />
          </div>
        </form>

        <div className="flex gap-3 p-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="btn btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {mutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function IncidentDetailModal({ incident, onClose, onEditInvestigation, onStatusTransition, onFileUpload }) {
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch full incident details to ensure attachments are up-to-date
  const { data: detailData } = useQuery({
    queryKey: ['incidents', incident.id],
    queryFn: () => incidentsAPI.getIncident(incident.id),
    enabled: !!incident?.id,
  });

  // Use the fetched detail data if available, otherwise fall back to passed incident
  const incidentData = detailData?.data || incident;

  const severityConfig = {
    minor: 'badge-low',
    moderate: 'badge-medium',
    serious: 'badge-high',
    critical: 'badge-critical',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-3xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Incident Details</h3>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-700 hover:text-slate-800 hover:bg-slate-50 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title & Status */}
          <div>
            <h2 className="text-xl font-bold text-slate-800 mb-3">{incidentData.title}</h2>
            <div className="flex flex-wrap gap-2">
              <span className={clsx('badge', severityConfig[incidentData.severity])}>
                {incidentData.severity}
              </span>
              <span className="badge bg-gray-500/20 text-gray-700 border border-gray-500/30">
                {incidentData.status.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Image Gallery Section */}
          {incidentData.attachments && incidentData.attachments.some(a => a.url && /\.(jpg|jpeg|png|gif)$/i.test(a.url)) && (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <PhotoIcon className="w-5 h-5" />
                Images
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {incidentData.attachments
                  .filter(a => a.url && /\.(jpg|jpeg|png|gif)$/i.test(a.url))
                  .map((image, i) => (
                    <div
                      key={i}
                      onClick={() => setPreviewImage(image.url)}
                      className="relative group cursor-pointer rounded-lg overflow-hidden bg-slate-100 aspect-square"
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <EyeIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Status Workflow Timeline */}
          <div className="glass rounded-xl p-4">
            <h4 className="text-xs font-semibold text-slate-600 mb-3 uppercase">Incident Workflow</h4>
            <div className="flex items-center justify-between text-sm">
              <div className="flex-1 text-center">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2',
                  incidentData.status === 'open' || ['investigating', 'pending_action', 'resolved', 'closed'].includes(incidentData.status)
                    ? 'bg-blue-500/20 text-blue-600 border-2 border-blue-500'
                    : 'bg-slate-100 text-slate-600 border-2 border-slate-200'
                )}>
                  🔴
                </div>
                <p className="text-xs text-slate-700">Open</p>
              </div>
              <div className={clsx('flex-1 h-0.5', incidentData.status !== 'open' ? 'bg-blue-500' : 'bg-slate-200')} />
              <div className="flex-1 text-center">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2',
                  ['investigating', 'pending_action', 'resolved', 'closed'].includes(incidentData.status)
                    ? 'bg-blue-500/20 text-blue-600 border-2 border-blue-500'
                    : 'bg-slate-100 text-slate-700 border-2 border-slate-200'
                )}>
                  🟡
                </div>
                <p className="text-xs text-slate-700">Investigating</p>
              </div>
              <div className={clsx('flex-1 h-0.5', ['pending_action', 'resolved', 'closed'].includes(incidentData.status) ? 'bg-blue-500' : 'bg-slate-200')} />
              <div className="flex-1 text-center">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2',
                  ['resolved', 'closed'].includes(incidentData.status)
                    ? 'bg-blue-500/20 text-blue-600 border-2 border-blue-500'
                    : 'bg-slate-100 text-slate-700 border-2 border-slate-200'
                )}>
                  🟢
                </div>
                <p className="text-xs text-slate-700">Resolved</p>
              </div>
              <div className={clsx('flex-1 h-0.5', incidentData.status === 'closed' ? 'bg-blue-500' : 'bg-slate-200')} />
              <div className="flex-1 text-center">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2',
                  incidentData.status === 'closed'
                    ? 'bg-blue-500/20 text-blue-600 border-2 border-blue-500'
                    : 'bg-slate-100 text-slate-700 border-2 border-slate-200'
                )}>
                  ⚫
                </div>
                <p className="text-xs text-slate-700">Closed</p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-700 mb-1">Type</p>
              <p className="font-medium text-slate-800">
                {incidentTypes.find(t => t.value === incidentData.type)?.label}
              </p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-700 mb-1">Location</p>
              <p className="font-medium text-slate-800">{incidentData.location || '-'}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-700 mb-1">Occurred At</p>
              <p className="font-medium text-slate-800">
                {formatDateTime(incidentData.occurredAt)}
              </p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-700 mb-1">Reported At</p>
              <p className="font-medium text-slate-800">
                {formatDateTime(incidentData.reportedAt)}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="glass rounded-xl p-4">
            <p className="text-sm text-slate-700 mb-2">Description</p>
            <p className="text-slate-800">{incidentData.description || 'No description provided'}</p>
          </div>

          {/* Investigation Details Section */}
          {incidentData.investigator || incidentData.investigationDue || incidentData.rootCause || incidentData.contributingFactors || incidentData.immediateActions ? (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5" />
                Investigation Details
              </h3>

              <div className="grid grid-cols-2 gap-3 mb-3">
                {incidentData.investigator && (
                  <div>
                    <p className="text-xs text-slate-700 mb-1">Investigator</p>
                    <p className="font-medium text-slate-800">{incidentData.investigator}</p>
                  </div>
                )}

                {incidentData.investigationDue && (
                  <div>
                    <p className="text-xs text-slate-700 mb-1">Investigation Due</p>
                    <p className={clsx(
                      "font-medium",
                      new Date(incidentData.investigationDue) < new Date()
                        ? "text-red-600"
                        : "text-slate-800"
                    )}>
                      {formatDate(incidentData.investigationDue)}
                      {new Date(incidentData.investigationDue) < new Date() && " (OVERDUE)"}
                    </p>
                  </div>
                )}
              </div>

              {incidentData.rootCause && (
                <div className="mt-3">
                  <p className="text-xs text-slate-700 mb-1">Root Cause</p>
                  <p className="text-slate-800">{incidentData.rootCause}</p>
                </div>
              )}

              {incidentData.contributingFactors && incidentData.contributingFactors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-slate-700 mb-2">Contributing Factors</p>
                  <ul className="list-disc list-inside text-slate-800 space-y-1">
                    {incidentData.contributingFactors.map((factor, i) => (
                      <li key={i}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}

              {incidentData.immediateActions && (
                <div className="mt-3">
                  <p className="text-xs text-slate-700 mb-1">Immediate Actions Taken</p>
                  <p className="text-slate-800">{incidentData.immediateActions}</p>
                </div>
              )}
            </div>
          ) : null}

          {/* People Involved Section */}
          {incidentData.involvedWorkers && incidentData.involvedWorkers.length > 0 || incidentData.witnesses && incidentData.witnesses.length > 0 ? (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5" />
                People Involved
              </h3>

              {incidentData.involvedWorkers && incidentData.involvedWorkers.length > 0 && (
                <div>
                  <p className="text-xs text-slate-700 mb-2 font-medium">👤 Workers Involved</p>
                  <div className="space-y-2">
                    {incidentData.involvedWorkers.map((worker, i) => (
                      <div key={i} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">
                          {worker.name?.charAt(0) || 'W'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{worker.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-700">{worker.role || 'Role not specified'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {incidentData.witnesses && incidentData.witnesses.length > 0 && (
                <div className={incidentData.involvedWorkers?.length > 0 ? 'mt-4 pt-4 border-t border-slate-200' : ''}>
                  <p className="text-xs text-slate-700 mb-2 font-medium">👁️ Witnesses</p>
                  <div className="space-y-2">
                    {incidentData.witnesses.map((witness, i) => (
                      <div key={i} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-sm font-bold text-amber-600 flex-shrink-0">
                          {witness.name?.charAt(0) || 'W'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{witness.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-700">{witness.contact || 'Contact not specified'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Corrective & Preventive Actions Section */}
          {(incidentData.correctiveActions && incidentData.correctiveActions.length > 0) ||
          (incidentData.preventiveActions && incidentData.preventiveActions.length > 0) ? (
            <div className="space-y-3">
              {incidentData.correctiveActions && incidentData.correctiveActions.length > 0 && (
                <div className="glass rounded-xl p-4">
                  <p className="text-sm text-slate-700 mb-2 font-medium">🔧 Corrective Actions</p>
                  <ul className="list-disc list-inside text-slate-800 space-y-1">
                    {incidentData.correctiveActions.map((action, i) => (
                      <li key={i}>{typeof action === 'string' ? action : action.action}</li>
                    ))}
                  </ul>
                </div>
              )}

              {incidentData.preventiveActions && incidentData.preventiveActions.length > 0 && (
                <div className="glass rounded-xl p-4">
                  <p className="text-sm text-slate-700 mb-2 font-medium">🛡️ Preventive Actions</p>
                  <ul className="list-disc list-inside text-slate-800 space-y-1">
                    {incidentData.preventiveActions.map((action, i) => (
                      <li key={i}>{typeof action === 'string' ? action : action.action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}

          {/* OSHA Compliance Section */}
          {incidentData.oshaReportable || incidentData.oshaReportNumber || incidentData.reportedToOsha ? (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <CheckBadgeIcon className="w-5 h-5" />
                OSHA Compliance
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-slate-700 mb-1">OSHA Reportable</p>
                  <p className={clsx(
                    "font-bold text-lg",
                    incidentData.oshaReportable ? "text-red-600" : "text-green-600"
                  )}>
                    {incidentData.oshaReportable ? "🔴 YES" : "🟢 NO"}
                  </p>
                </div>

                {incidentData.oshaReportNumber && (
                  <div>
                    <p className="text-xs text-slate-700 mb-1">OSHA Report #</p>
                    <p className="font-medium text-slate-800">{incidentData.oshaReportNumber}</p>
                  </div>
                )}

                {incidentData.reportedToOsha && (
                  <div>
                    <p className="text-xs text-slate-700 mb-1">Reported to OSHA</p>
                    <p className="font-medium text-slate-800">
                      {formatDate(incidentData.reportedToOsha)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Attachments Section */}
          {incidentData.attachments && incidentData.attachments.length > 0 ? (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <PaperClipIcon className="w-5 h-5" />
                Attachments ({incidentData.attachments.length})
              </h3>

              <div className="space-y-2">
                {incidentData.attachments.map((attachment, i) => {
                  const isImage = attachment.url && /\.(jpg|jpeg|png|gif)$/i.test(attachment.url);
                  return (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <DocumentIcon className="w-5 h-5 text-slate-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 truncate">{attachment.name || 'Attachment'}</p>
                          <p className="text-xs text-slate-700">
                            {attachment.size ? `${(attachment.size / 1024 / 1024).toFixed(2)} MB` : ''}
                            {attachment.uploadedAt && ` • ${formatDate(attachment.uploadedAt)}`}
                          </p>
                        </div>
                      </div>
                      <div className="ml-2 flex items-center gap-1 flex-shrink-0">
                        {isImage && (
                          <button
                            onClick={() => setPreviewImage(attachment.url)}
                            className="p-2 rounded-lg text-green-600 hover:bg-green-500/20 transition-colors"
                            title="View Image"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        )}
                        <a
                          href={attachment.url}
                          download={attachment.name}
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-500/20 transition-colors"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Linked Alerts Section */}
          {incidentData.alerts && incidentData.alerts.length > 0 ? (
            <div className="glass rounded-xl p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <BellAlertIcon className="w-5 h-5" />
                Associated Alerts ({incidentData.alerts.length})
              </h3>

              <div className="space-y-2">
                {incidentData.alerts.slice(0, 10).map((alert, i) => (
                  <div key={i} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={clsx(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          alert.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-600' :
                          alert.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-600' :
                          alert.severity === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-600' :
                          'bg-blue-500/20 text-blue-600'
                        )}>
                          {alert.severity}
                        </span>
                      </div>
                      <p className="font-medium text-slate-800">{alert.type?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-700">
                        {formatDateTime(alert.detectedAt)}
                      </p>
                    </div>
                    {alert.snapshotUrl && (
                      <button
                        className="ml-2 w-10 h-10 rounded-lg bg-slate-200 hover:bg-slate-300 transition-colors flex-shrink-0"
                        title="View snapshot"
                        onClick={() => window.open(alert.snapshotUrl, '_blank')}
                      >
                        <PhotoIcon className="w-5 h-5 mx-auto text-slate-600" />
                      </button>
                    )}
                  </div>
                ))}
                {incidentData.alerts.length > 10 && (
                  <p className="text-xs text-slate-700 text-center py-2">
                    +{incidentData.alerts.length - 10} more alerts
                  </p>
                )}
              </div>
            </div>
          ) : null}

          {/* Audit Trail Section */}
          <div className="glass rounded-xl p-4">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <ClockIcon className="w-5 h-5" />
              Audit Trail
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <p className="text-slate-600">Created</p>
                <p className="text-slate-800 font-medium">
                  {formatDateTime(incidentData.createdAt)}
                </p>
              </div>

              {incidentData.createdBy && (
                <div className="flex justify-between">
                  <p className="text-slate-600">Created By</p>
                  <p className="text-slate-800 font-medium">
                    {incidentData.createdBy.firstName} {incidentData.createdBy.lastName}
                  </p>
                </div>
              )}

              {incidentData.closedAt && (
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <p className="text-slate-600">Closed</p>
                  <p className="text-slate-800 font-medium">
                    {formatDateTime(incidentData.closedAt)}
                  </p>
                </div>
              )}

              {incidentData.closedBy && (
                <div className="flex justify-between">
                  <p className="text-slate-600">Closed By</p>
                  <p className="text-slate-800 font-medium">
                    {incidentData.closedByUser
                      ? `${incidentData.closedByUser.firstName} ${incidentData.closedByUser.lastName}`
                      : incidentData.closedBy}
                  </p>
                </div>
              )}

              {incidentData.updatedAt && (
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <p className="text-slate-600">Last Modified</p>
                  <p className="text-slate-800 font-medium">
                    {formatDateTime(incidentData.updatedAt)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
          <button onClick={onFileUpload} className="btn btn-secondary flex items-center gap-2">
            <PaperClipIcon className="w-4 h-4" />
            Upload Files
          </button>
          <button onClick={onStatusTransition} className="btn btn-warning">Change Status</button>
          <button onClick={onEditInvestigation} className="btn btn-primary">Edit Investigation</button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setPreviewImage(null)}>
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
          <div className="relative bg-slate-900 rounded-xl overflow-hidden max-w-4xl max-h-[90vh] animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <img src={previewImage} alt="Preview" className="w-full h-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}

function EditInvestigationModal({ incident, onClose, onSuccess }) {
  const queryClient = useQueryClient();

  // Fetch fresh incident details to ensure data is up-to-date
  const { data: detailData } = useQuery({
    queryKey: ['incidents', incident.id],
    queryFn: () => incidentsAPI.getIncident(incident.id),
    enabled: !!incident?.id,
  });

  // Use the fetched detail data if available, otherwise fall back to passed incident
  const incidentData = detailData?.data || incident;

  const [formData, setFormData] = useState({
    investigator: incidentData.investigator || '',
    investigationDue: incidentData.investigationDue ? new Date(incidentData.investigationDue).toISOString().slice(0, 16) : '',
    rootCause: incidentData.rootCause || '',
    contributingFactors: incidentData.contributingFactors || [],
    immediateActions: incidentData.immediateActions || '',
    involvedWorkers: incidentData.involvedWorkers || [],
    witnesses: incidentData.witnesses || [],
    correctiveActions: incidentData.correctiveActions || [],
    preventiveActions: incidentData.preventiveActions || [],
    oshaReportable: incidentData.oshaReportable || false,
    oshaReportNumber: incidentData.oshaReportNumber || '',
    reportedToOsha: incidentData.reportedToOsha ? new Date(incidentData.reportedToOsha).toISOString().slice(0, 10) : '',
  });

  const [newFactor, setNewFactor] = useState('');
  const [newWorker, setNewWorker] = useState({ name: '', role: '' });
  const [newWitness, setNewWitness] = useState({ name: '', contact: '' });
  const [newCorrectiveAction, setNewCorrectiveAction] = useState('');
  const [newPreventiveAction, setNewPreventiveAction] = useState('');

  // Update form when fresh incident data arrives from the query
  useEffect(() => {
    setFormData({
      investigator: incidentData.investigator || '',
      investigationDue: incidentData.investigationDue ? new Date(incidentData.investigationDue).toISOString().slice(0, 16) : '',
      rootCause: incidentData.rootCause || '',
      contributingFactors: incidentData.contributingFactors || [],
      immediateActions: incidentData.immediateActions || '',
      involvedWorkers: incidentData.involvedWorkers || [],
      witnesses: incidentData.witnesses || [],
      correctiveActions: incidentData.correctiveActions || [],
      preventiveActions: incidentData.preventiveActions || [],
      oshaReportable: incidentData.oshaReportable || false,
      oshaReportNumber: incidentData.oshaReportNumber || '',
      reportedToOsha: incidentData.reportedToOsha ? new Date(incidentData.reportedToOsha).toISOString().slice(0, 10) : '',
    });
  }, [detailData]);

  const updateMutation = useMutation({
    mutationFn: (data) => incidentsAPI.updateIncident(incidentData.id, data),
    onSuccess: () => {
      toast.success('Investigation updated successfully');
      onSuccess();
    },
    onError: (error) => {
      console.error('Update incident error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to update incident';
      toast.error(errorMsg);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Format dates as ISO strings for backend validation
    const dataToSend = {
      ...formData,
    };

    // Convert datetime-local format (YYYY-MM-DDTHH:mm) to ISO string
    if (formData.investigationDue) {
      dataToSend.investigationDue = new Date(formData.investigationDue).toISOString();
    } else {
      dataToSend.investigationDue = null;
    }

    // Convert date format (YYYY-MM-DD) to ISO string
    if (formData.reportedToOsha) {
      dataToSend.reportedToOsha = new Date(formData.reportedToOsha).toISOString();
    } else {
      dataToSend.reportedToOsha = null;
    }

    console.log('[Incident Update] Sending data to backend:', dataToSend);
    console.log('[Incident Update] investigationDue type:', typeof dataToSend.investigationDue);
    console.log('[Incident Update] reportedToOsha type:', typeof dataToSend.reportedToOsha);

    updateMutation.mutate(dataToSend);
  };

  const addFactor = () => {
    if (newFactor.trim()) {
      setFormData({
        ...formData,
        contributingFactors: [...formData.contributingFactors, newFactor],
      });
      setNewFactor('');
    }
  };

  const removeFactor = (index) => {
    setFormData({
      ...formData,
      contributingFactors: formData.contributingFactors.filter((_, i) => i !== index),
    });
  };

  const addWorker = () => {
    if (newWorker.name.trim()) {
      setFormData({
        ...formData,
        involvedWorkers: [...formData.involvedWorkers, newWorker],
      });
      setNewWorker({ name: '', role: '' });
    }
  };

  const removeWorker = (index) => {
    setFormData({
      ...formData,
      involvedWorkers: formData.involvedWorkers.filter((_, i) => i !== index),
    });
  };

  const addWitness = () => {
    if (newWitness.name.trim()) {
      setFormData({
        ...formData,
        witnesses: [...formData.witnesses, newWitness],
      });
      setNewWitness({ name: '', contact: '' });
    }
  };

  const removeWitness = (index) => {
    setFormData({
      ...formData,
      witnesses: formData.witnesses.filter((_, i) => i !== index),
    });
  };

  const addCorrectiveAction = () => {
    if (newCorrectiveAction.trim()) {
      setFormData({
        ...formData,
        correctiveActions: [...formData.correctiveActions, newCorrectiveAction],
      });
      setNewCorrectiveAction('');
    }
  };

  const removeCorrectiveAction = (index) => {
    setFormData({
      ...formData,
      correctiveActions: formData.correctiveActions.filter((_, i) => i !== index),
    });
  };

  const addPreventiveAction = () => {
    if (newPreventiveAction.trim()) {
      setFormData({
        ...formData,
        preventiveActions: [...formData.preventiveActions, newPreventiveAction],
      });
      setNewPreventiveAction('');
    }
  };

  const removePreventiveAction = (index) => {
    setFormData({
      ...formData,
      preventiveActions: formData.preventiveActions.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-4xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Edit Investigation</h3>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-700 hover:text-slate-800 hover:bg-slate-50 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Investigation Details Section */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5" />
              Investigation Details
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Investigator</label>
                <input
                  type="text"
                  value={formData.investigator}
                  onChange={(e) => setFormData({ ...formData, investigator: e.target.value })}
                  className="input w-full"
                  placeholder="e.g., John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Investigation Due</label>
                <input
                  type="datetime-local"
                  value={formData.investigationDue}
                  onChange={(e) => setFormData({ ...formData, investigationDue: e.target.value })}
                  className="input w-full"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Root Cause</label>
              <textarea
                value={formData.rootCause}
                onChange={(e) => setFormData({ ...formData, rootCause: e.target.value })}
                className="input w-full min-h-[100px] resize-none"
                placeholder="Describe the root cause of the incident..."
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Contributing Factors</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newFactor}
                  onChange={(e) => setNewFactor(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFactor())}
                  className="input flex-1"
                  placeholder="Add a contributing factor..."
                />
                <button
                  type="button"
                  onClick={addFactor}
                  className="btn btn-secondary px-4"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.contributingFactors.map((factor, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-800">{factor}</span>
                    <button
                      type="button"
                      onClick={() => removeFactor(idx)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Immediate Actions Taken</label>
              <textarea
                value={formData.immediateActions}
                onChange={(e) => setFormData({ ...formData, immediateActions: e.target.value })}
                className="input w-full min-h-[100px] resize-none"
                placeholder="Describe actions taken immediately after the incident..."
              />
            </div>
          </div>

          {/* People Involved Section */}
          <div className="border-t border-slate-200 pt-6">
            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <UserGroupIcon className="w-5 h-5" />
              People Involved
            </h4>

            <div className="space-y-4">
              {/* Workers */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Workers Involved</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newWorker.name}
                    onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
                    className="input flex-1"
                    placeholder="Worker name"
                  />
                  <input
                    type="text"
                    value={newWorker.role}
                    onChange={(e) => setNewWorker({ ...newWorker, role: e.target.value })}
                    className="input flex-1"
                    placeholder="Role"
                  />
                  <button
                    type="button"
                    onClick={addWorker}
                    className="btn btn-secondary px-4"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.involvedWorkers.map((worker, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{worker.name}</p>
                        <p className="text-xs text-slate-700">{worker.role}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeWorker(idx)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Witnesses */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Witnesses</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newWitness.name}
                    onChange={(e) => setNewWitness({ ...newWitness, name: e.target.value })}
                    className="input flex-1"
                    placeholder="Witness name"
                  />
                  <input
                    type="text"
                    value={newWitness.contact}
                    onChange={(e) => setNewWitness({ ...newWitness, contact: e.target.value })}
                    className="input flex-1"
                    placeholder="Contact info"
                  />
                  <button
                    type="button"
                    onClick={addWitness}
                    className="btn btn-secondary px-4"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.witnesses.map((witness, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{witness.name}</p>
                        <p className="text-xs text-slate-700">{witness.contact}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeWitness(idx)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="border-t border-slate-200 pt-6">
            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CheckBadgeIcon className="w-5 h-5" />
              Corrective & Preventive Actions
            </h4>

            <div className="space-y-4">
              {/* Corrective Actions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Corrective Actions</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newCorrectiveAction}
                    onChange={(e) => setNewCorrectiveAction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCorrectiveAction())}
                    className="input flex-1"
                    placeholder="Add a corrective action..."
                  />
                  <button
                    type="button"
                    onClick={addCorrectiveAction}
                    className="btn btn-secondary px-4"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.correctiveActions.map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-800">{typeof action === 'string' ? action : action.action}</span>
                      <button
                        type="button"
                        onClick={() => removeCorrectiveAction(idx)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preventive Actions */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Preventive Actions</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newPreventiveAction}
                    onChange={(e) => setNewPreventiveAction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPreventiveAction())}
                    className="input flex-1"
                    placeholder="Add a preventive action..."
                  />
                  <button
                    type="button"
                    onClick={addPreventiveAction}
                    className="btn btn-secondary px-4"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.preventiveActions.map((action, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-800">{typeof action === 'string' ? action : action.action}</span>
                      <button
                        type="button"
                        onClick={() => removePreventiveAction(idx)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* OSHA Compliance Section */}
          <div className="border-t border-slate-200 pt-6">
            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <CheckBadgeIcon className="w-5 h-5" />
              OSHA Compliance
            </h4>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  checked={formData.oshaReportable}
                  onChange={(e) => setFormData({ ...formData, oshaReportable: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label className="text-sm font-medium text-slate-700">This is an OSHA reportable incident</label>
              </div>

              {formData.oshaReportable && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">OSHA Report Number</label>
                    <input
                      type="text"
                      value={formData.oshaReportNumber}
                      onChange={(e) => setFormData({ ...formData, oshaReportNumber: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 2026-00145"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Date Reported to OSHA</label>
                    <input
                      type="date"
                      value={formData.reportedToOsha}
                      onChange={(e) => setFormData({ ...formData, reportedToOsha: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={updateMutation.isPending} className="btn btn-primary">
            {updateMutation.isPending ? 'Saving...' : 'Save Investigation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusTransitionModal({ incident, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [newStatus, setNewStatus] = useState(incident.status);
  const [statusReason, setStatusReason] = useState('');
  const [showWarning, setShowWarning] = useState(false);

  // Fetch fresh incident details to ensure investigation data is up-to-date
  const { data: detailData } = useQuery({
    queryKey: ['incidents', incident.id],
    queryFn: () => incidentsAPI.getIncident(incident.id),
    enabled: !!incident?.id,
  });

  // Use the fetched detail data if available, otherwise fall back to passed incident
  const incidentData = detailData?.data || incident;

  // Define valid status transitions
  const statusTransitions = {
    open: ['investigating', 'resolved', 'closed'],
    investigating: ['pending_action', 'resolved', 'closed'],
    pending_action: ['investigating', 'resolved', 'closed'],
    resolved: ['investigating', 'closed'],
    closed: [],
  };

  // Define status info
  const statusInfo = {
    open: { label: 'Open', color: 'red', icon: '🔴', description: 'Incident reported, awaiting investigation' },
    investigating: { label: 'Investigating', color: 'yellow', icon: '🟡', description: 'Investigation in progress' },
    pending_action: { label: 'Pending Action', color: 'orange', icon: '🟠', description: 'Waiting for action items to be completed' },
    resolved: { label: 'Resolved', color: 'green', icon: '🟢', description: 'Investigation complete, actions assigned' },
    closed: { label: 'Closed', color: 'gray', icon: '⚫', description: 'Incident fully resolved and closed' },
  };

  const currentStatus = statusInfo[incidentData.status];
  const nextStatus = statusInfo[newStatus];
  const validTransitions = statusTransitions[incidentData.status] || [];

  // Validation rules
  const getWarnings = () => {
    const warnings = [];

    // Warn if closing without investigation data
    if ((newStatus === 'resolved' || newStatus === 'closed') && !incidentData.rootCause) {
      warnings.push('⚠️ No root cause documented');
    }
    if ((newStatus === 'resolved' || newStatus === 'closed') && !incidentData.investigator) {
      warnings.push('⚠️ No investigator assigned');
    }
    if ((newStatus === 'resolved' || newStatus === 'closed') && (!incidentData.correctiveActions || incidentData.correctiveActions.length === 0)) {
      warnings.push('⚠️ No corrective actions defined');
    }
    if ((newStatus === 'resolved' || newStatus === 'closed') && !incidentData.investigationDue) {
      warnings.push('⚠️ No investigation deadline set');
    }

    return warnings;
  };

  const warnings = getWarnings();
  const canTransition = validTransitions.includes(newStatus);
  const hasWarnings = warnings.length > 0;

  const updateMutation = useMutation({
    mutationFn: (data) => incidentsAPI.updateIncident(incidentData.id, data),
    onSuccess: () => {
      toast.success(`Status changed to ${nextStatus.label}`);
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to change status');
    },
  });

  const handleStatusChange = (e) => {
    setNewStatus(e.target.value);
    setStatusReason('');
    setShowWarning(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if transition is valid
    if (!canTransition) {
      toast.error(`Cannot transition from ${currentStatus.label} to ${nextStatus.label}`);
      return;
    }

    // Show warning if needed
    if (hasWarnings && !showWarning) {
      setShowWarning(true);
      return;
    }

    // Submit status change
    updateMutation.mutate({
      status: newStatus,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Change Incident Status</h3>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-700 hover:text-slate-800 hover:bg-slate-50 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Current Status */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Current Status</h4>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{currentStatus.icon}</span>
                <div>
                  <p className="font-semibold text-slate-800">{currentStatus.label}</p>
                  <p className="text-sm text-slate-700">{currentStatus.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Transition */}
          <div className="border-t border-slate-200 pt-6">
            <h4 className="font-semibold text-slate-800 mb-3">Change To</h4>
            <div className="space-y-3">
              {validTransitions.length > 0 ? (
                validTransitions.map(status => {
                  const statusDef = statusInfo[status];
                  return (
                    <label key={status} className="flex items-start gap-3 p-3 rounded-lg border-2 border-slate-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all" style={{ borderColor: newStatus === status ? '#3b82f6' : undefined, backgroundColor: newStatus === status ? 'rgba(59, 130, 246, 0.05)' : undefined }}>
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={newStatus === status}
                        onChange={handleStatusChange}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{statusDef.icon}</span>
                          <p className="font-semibold text-slate-800">{statusDef.label}</p>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{statusDef.description}</p>
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-600">
                  <p>No valid status transitions available for {currentStatus.label}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Reason */}
          {newStatus !== incidentData.status && (
            <div className="border-t border-slate-200 pt-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Reason for Status Change (Optional)</label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                className="input w-full min-h-[100px] resize-none"
                placeholder="Document why this status change is being made..."
              />
            </div>
          )}

          {/* Investigation Completion Warning */}
          {hasWarnings && showWarning && (
            <div className="border-t border-slate-200 pt-6">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                  Investigation Incomplete
                </h4>
                <ul className="space-y-1 text-sm text-yellow-800">
                  {warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
                <p className="text-xs text-yellow-700 mt-3">
                  We recommend completing the investigation before closing or resolving. You can still proceed if needed.
                </p>
              </div>
            </div>
          )}

          {/* Workflow Info */}
          <div className="border-t border-slate-200 pt-6">
            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <ClockIcon className="w-5 h-5" />
              Workflow Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-700 mb-1">Status Created</p>
                <p className="font-medium text-slate-800">{formatDate(incidentData.createdAt)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-700 mb-1">Last Modified</p>
                <p className="font-medium text-slate-800">{formatDate(incidentData.updatedAt)}</p>
              </div>
              {incidentData.investigationDue && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-700 mb-1">Investigation Due</p>
                  <p className={clsx(
                    "font-medium",
                    new Date(incidentData.investigationDue) < new Date()
                      ? "text-red-600"
                      : "text-slate-800"
                  )}>
                    {formatDate(incidentData.investigationDue)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          {hasWarnings && showWarning ? (
            <button
              onClick={() => setShowWarning(false)}
              className="btn btn-secondary"
            >
              Back to Form
            </button>
          ) : null}
          {canTransition && newStatus !== incidentData.status && (
            <button
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
              className="btn btn-primary"
            >
              {updateMutation.isPending ? 'Changing...' : `Change to ${nextStatus.label}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FileUploadModal({ incident, onClose, onSuccess }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const results = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          // Upload file to backend
          const response = await fetch('/api/incidents/upload', {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
          }

          const data = await response.json();
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
          results.push({
            name: file.name,
            size: file.size,
            uploadedAt: new Date().toISOString(),
            url: data.url, // ✅ Actual server URL, not blob URL
            id: data.id,
          });
        } catch (error) {
          console.error('File upload error:', error);
          throw error;
        }
      }
      return results;
    },
    onSuccess: async (data) => {
      // Update incident with new attachments
      const currentAttachments = incident.attachments || [];
      const newAttachments = [...currentAttachments, ...data];

      try {
        const response = await fetch(`/api/incidents/${incident.id}/attachments`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ attachments: newAttachments }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to save attachments: ${response.statusText}`);
        }

        toast.success(`${data.length} file(s) uploaded successfully`);
        onSuccess();
      } catch (error) {
        console.error('Attachment save error:', error);
        toast.error(error.message || 'Failed to save attachments');
        throw error;
      }
    },
    onError: (error) => {
      toast.error(error.message || 'File upload failed');
    },
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
  };

  const handleChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(Array.from(files));
    }
  };

  const handleFiles = (files) => {
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const maxSize = 50 * 1024 * 1024; // 50MB
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      if (file.size > maxSize) {
        toast.error(`${file.name} exceeds 50MB limit`);
        return false;
      }

      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} file type not supported`);
        return false;
      }

      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    validFiles.forEach(file => {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
    });
  };

  const removeFile = (fileName) => {
    setSelectedFiles(prev => prev.filter(f => f.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    uploadMutation.mutate(selectedFiles);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={onClose} />

      <div className="relative glass-card w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Upload Incident Files</h3>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-700 hover:text-slate-800 hover:bg-slate-50 transition-all">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Drag & Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={clsx(
              'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-300 bg-slate-50 hover:border-slate-400'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleChange}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
            />

            <div className="flex flex-col items-center gap-3">
              <PaperClipIcon className="w-12 h-12 text-slate-600" />
              <div>
                <p className="font-semibold text-slate-800">Drag files here or click to select</p>
                <p className="text-sm text-slate-600 mt-1">
                  Supported: Images (JPG, PNG, GIF), PDF, Documents (DOC, DOCX)
                </p>
                <p className="text-xs text-slate-700 mt-1">Maximum file size: 50MB</p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-secondary mt-4"
              >
                Select Files
              </button>
            </div>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-800 mb-3">Selected Files ({selectedFiles.length})</h4>
              <div className="space-y-2">
                {selectedFiles.map((file) => {
                  const progress = uploadProgress[file.name] || 0;
                  return (
                    <div key={file.name} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <DocumentIcon className="w-5 h-5 text-slate-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800 truncate">{file.name}</p>
                            <p className="text-xs text-slate-700">
                              {(file.size / 1024 / 1024).toFixed(1)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(file.name)}
                          className="text-red-600 hover:bg-red-50 p-1 rounded flex-shrink-0"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Progress Bar */}
                      {progress > 0 && progress < 100 && (
                        <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="bg-blue-500 h-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                      {progress === 100 && (
                        <div className="text-xs text-green-600 font-medium">✓ Ready to upload</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* File Guidelines */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">File Upload Guidelines</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>✓ Upload evidence photos of the incident</li>
              <li>✓ Attach investigation reports and documents</li>
              <li>✓ Include witness statements and forms</li>
              <li>✓ Files are attached to this incident permanently</li>
              <li>✓ All team members can view uploaded files</li>
            </ul>
          </div>
        </form>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploadMutation.isPending || selectedFiles.length === 0}
            className="btn btn-primary"
          >
            {uploadMutation.isPending ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
